/**
 * announcementService — Firestore reads/writes for the `announcements`
 * collection (the Today page's teacher announcements). Behavior unchanged.
 */

import { db } from '../firebase';
import {
	collection,
	addDoc,
	getDocs,
	query,
	orderBy,
	Timestamp,
} from 'firebase/firestore';

const COLLECTION = 'announcements';

// Newest first.
export async function fetchAnnouncements() {
	const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));
	const snapshot = await getDocs(q);
	return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Customer-facing: the currently-visible announcements, newest first — active
// and not past their expiresAt. Reuses the single-field createdAt index (no
// composite index needed) and filters active/expiry client-side, mirroring the
// AnnouncementBanner's visibility rule. Feeds both the Home hub and the
// read-only Announcements feed. `customer` role may read the collection
// (firestore.rules: announcements read = isSignedIn()).
export async function fetchActiveAnnouncements() {
	const all = await fetchAnnouncements();
	const now = Date.now();
	const visible = all.filter((a) => {
		if (a.active === false) return false;
		const exp = a.expiresAt?.toDate ? a.expiresAt.toDate().getTime() : null;
		return exp === null || exp > now;
	});

	/* PRIORITY FIRST, THEN NEWEST — matching AnnouncementBanner (which already
	   orderBy('priority','desc')) and matching what the admin composer promises
	   staff in its own help text: "Higher priority shows first (1-10)".
	   Until now the customer surfaces sorted by date alone, so an announcement
	   deliberately marked priority 10 sat below a trivial newer one, and the
	   same set appeared in two different orders depending on which surface you
	   looked at. Fixed here rather than in the feed because BOTH customer
	   readers go through this function — the Announcements page and the Home
	   hub, which shows the top two.

	   Sorted CLIENT-SIDE on purpose: adding orderBy('priority') to the query
	   would demand a composite index, and this function's whole shape (fetch by
	   createdAt, filter in memory) exists to avoid one. */
	return visible.sort((a, b) => {
		const pa = Number.isFinite(a.priority) ? a.priority : 1;
		const pb = Number.isFinite(b.priority) ? b.priority : 1;
		if (pa !== pb) return pb - pa;
		const ta = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
		const tb = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
		return tb - ta;
	});
}

/* The four announcement types staff can choose (ManageAnnouncements: Info /
   Success / Warning / Urgent), mapped to the feed's presentation. The SEMANTICS
   mirror AnnouncementBanner deliberately — a family should not learn one
   vocabulary of colour and icon on the portal banner and a different one on the
   announcements page. The banner renders through react-bootstrap variants and
   is left alone; this maps the same four types onto the customer shell's
   theme tokens instead. Note the urgent type is `urgent`, NOT `danger` —
   `danger` is the Bootstrap variant the banner translates it into, and keying
   on it here would silently fall through to Info. */
const TONES = {
	info: { icon: 'fa-circle-info', token: 'var(--cyv-teal-600, #2f7d77)', label: 'Info' },
	success: { icon: 'fa-circle-check', token: 'var(--cyv-success, #4f8a5b)', label: 'Good news' },
	warning: { icon: 'fa-circle-exclamation', token: 'var(--cyv-warning, #b3801f)', label: 'Heads up' },
	urgent: { icon: 'fa-triangle-exclamation', token: 'var(--cyv-danger, #a8443c)', label: 'Important' },
};

export function announcementTone(type) {
	return TONES[type] || TONES.info;
}

export async function addAnnouncement(message, createdBy) {
	return addDoc(collection(db, COLLECTION), {
		message,
		createdBy,
		createdAt: Timestamp.now(),
	});
}
